/**
 * ge-cart-drawer.js — Horizon-parity cart drawer for Geruchsentferner
 */
(function () {
  'use strict';

  var root, bodyEl, badgeEl, footerEl, subtotalEl, noteEl, discountSummaryEl, discountCodesEl;
  var icons = { delete: '', minus: '', plus: '', close: '', discount: '' };
  var CART_SECTION = 'ge-cart-data';
  var lastCart = null;

  function money(cents) {
    return 'CHF ' + ((Number(cents) || 0) / 100).toFixed(2);
  }
  function esc(t) {
    return String(t || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isCartPath(pathname) {
    return pathname === '/cart' || pathname === '/cart/';
  }

  function openDrawer() {
    if (!root) return;
    var sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    if (sbw > 0) {
      document.documentElement.style.setProperty('--ge-scrollbar-compensation', sbw + 'px');
      document.body.style.paddingRight = sbw + 'px';
    }
    document.documentElement.classList.add('ge-cart-drawer-open');
    document.body.classList.add('ge-cart-drawer-open');
    root.classList.remove('is-closing');
    root.hidden = false;
    void root.offsetWidth;
    requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
  }
  function closeDrawer() {
    if (!root) return;
    root.classList.add('is-closing');
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ge-cart-drawer-open');
    document.body.classList.remove('ge-cart-drawer-open');
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--ge-scrollbar-compensation');
    setTimeout(function () {
      if (!root.classList.contains('is-open')) {
        root.hidden = true;
        root.classList.remove('is-closing');
      }
    }, 320);
  }

  function openCartInsteadOfPage(e) {
    if (e) {
      e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
    }
    return refresh(true);
  }

  function interceptCartPageLinks() {
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a[href]');
      if (!a) return;
      if (a.hasAttribute('data-ge-cart-open')) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      var url;
      try { url = new URL(href, window.location.origin); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (!isCartPath(url.pathname)) return;
      openCartInsteadOfPage(e);
    }, true);
  }

  function handleCartLanding() {
    // /cart → home + open drawer
    if (isCartPath(window.location.pathname)) {
      try { sessionStorage.setItem('ge-open-cart', '1'); } catch (e) {}
      window.location.replace('/?cart=open');
      return;
    }
    var open = false;
    try {
      if (new URLSearchParams(window.location.search).get('cart') === 'open') open = true;
      if (sessionStorage.getItem('ge-open-cart') === '1') {
        sessionStorage.removeItem('ge-open-cart');
        open = true;
      }
    } catch (e) {}
    if (!open) return;
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.has('cart')) {
        url.searchParams.delete('cart');
        var qs = url.searchParams.toString();
        history.replaceState(null, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
      }
    } catch (e) {}
    refresh(true);
  }

  function variantHtml(item) {
    var opts = item.options_with_values || [];
    var onlyDefault = item.product_has_only_default_variant;
    if (onlyDefault || !opts.length) {
      if (item.variant_title && item.variant_title !== 'Default Title' && !onlyDefault) {
        return (
          '<div class="cart-items__variants-wrapper">' +
            '<p class="cart-secondary-typography">' + esc(item.variant_title) + '</p>' +
          '</div>'
        );
      }
      return '<div class="cart-items__variants-wrapper"></div>';
    }
    var parts = opts.map(function (o, i) {
      return (
        '<div class="cart-items__variant">' +
          '<dt class="visually-hidden">' + esc(o.name) + ':</dt>' +
          '<dd>' + esc(o.value) + (i < opts.length - 1 ? ',&nbsp;' : '') + '</dd>' +
        '</div>'
      );
    }).join('');
    return (
      '<div class="cart-items__variants-wrapper">' +
        '<dl class="cart-items__variants">' + parts + '</dl>' +
      '</div>'
    );
  }

  function itemHtml(item) {
    var img = item.image
      ? '<img class="cart-items__media-image" src="' + esc(item.image) + '" alt="" width="64" height="64" loading="lazy">'
      : '';
    var sku = item.sku
      ? '<p class="ts-cart-sku">Art.-Nr. ' + esc(item.sku) + '</p>'
      : '';
    var unit = item.final_price != null ? item.final_price : item.price;

    return (
      '<div class="cart-items__table-row cart-items__table-row--full-width-variants" data-ge-cart-key="' + esc(item.key) + '">' +
        '<div class="cart-items__media">' +
          '<a href="' + esc(item.url) + '" class="cart-items__media-container" style="--ratio:1" tabindex="-1">' + img + '</a>' +
        '</div>' +
        '<div class="cart-items__details cart-primary-typography">' +
          '<div class="cart-items__product-info">' +
            '<a href="' + esc(item.url) + '" class="cart-items__title">' + esc(item.product_title) + '</a>' +
            sku +
          '</div>' +
          variantHtml(item) +
          '<div class="cart-items__unit-price-wrapper cart-secondary-typography">' +
            '<span>' + money(unit) + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="cart-items__price cart-secondary-typography">' + money(item.final_line_price) + '</div>' +
        '<div class="cart-items__quantity">' +
          '<div class="cart-items__quantity-controls">' +
            '<div class="quantity-selector-wrapper">' +
              '<div class="quantity-selector">' +
                '<button type="button" class="button quantity-minus button-unstyled" data-ge-qty-minus aria-label="Weniger">' +
                  '<span class="svg-wrapper">' + icons.minus + '</span>' +
                '</button>' +
                '<input type="number" min="0" value="' + item.quantity + '" data-ge-qty-input data-key="' + esc(item.key) + '" aria-label="Menge">' +
                '<button type="button" class="button quantity-plus button-unstyled" data-ge-qty-plus aria-label="Mehr">' +
                  '<span class="svg-wrapper">' + icons.plus + '</span>' +
                '</button>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="button cart-items__remove" data-ge-cart-remove data-key="' + esc(item.key) + '" aria-label="Entfernen">' +
              icons.delete +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function updateHeaderBubbles(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
      el.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    });
  }

  function normalizeDiscountApps(cart) {
    var raw = cart.cart_level_discount_applications || [];
    return raw.map(function (d) {
      var app = d.discount_application || d;
      return {
        type: app.type || d.type,
        title: app.title || d.title,
        total_allocated_amount:
          app.total_allocated_amount != null
            ? app.total_allocated_amount
            : d.amount != null
              ? d.amount
              : d.total_allocated_amount,
      };
    }).filter(function (d) { return d.title; });
  }

  function appliedDiscountCodes(cart) {
    var codes = [];
    function pushCode(title) {
      if (!title) return;
      if (codes.indexOf(title) === -1) codes.push(title);
    }
    normalizeDiscountApps(cart).forEach(function (d) {
      if (!d.type || d.type === 'discount_code') pushCode(d.title);
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var app = a.discount_application || {};
        if (app.type === 'discount_code') pushCode(app.title);
      });
      (item.discounts || []).forEach(function (d) {
        if (d.title) pushCode(d.title);
      });
    });
    return codes;
  }

  function discountSummaryHtml(cart) {
    var apps = normalizeDiscountApps(cart);
    if (!apps.length) return '';
    var html =
      '<span class="cart-totals__item cart-totals__original">' +
        '<span class="cart-totals__original-label">Zwischensumme</span>' +
        '<span class="cart-totals__original-value cart-secondary-typography">' +
          esc(money(cart.items_subtotal_price)) +
        '</span>' +
      '</span>' +
      '<ul class="cart-discounts" role="list" aria-label="Rabatte">';
    apps.forEach(function (d) {
      html +=
        '<li class="cart-discounts__item">' +
          '<span class="cart-discounts__label">' +
            (icons.discount || '') +
            esc(d.title) +
          '</span>' +
          '<span class="cart-discounts__value cart-secondary-typography">-' +
            esc(money(d.total_allocated_amount)) +
          '</span>' +
        '</li>';
    });
    html += '</ul>';
    return html;
  }

  function discountPillsHtml(codes) {
    return codes.map(function (code) {
      return (
        '<li class="cart-discount__pill" data-discount-code="' + esc(code) + '">' +
          '<p class="cart-discount__pill-code">' + esc(code) + '</p>' +
          '<button type="button" class="cart-discount__pill-remove" data-ge-discount-remove data-code="' +
            esc(code) +
            '" aria-label="Rabatt ' + esc(code) + ' entfernen">' +
            (icons.close || '×') +
          '</button>' +
        '</li>'
      );
    }).join('');
  }

  function renderDiscounts(cart) {
    var codes = appliedDiscountCodes(cart);
    if (discountSummaryEl) discountSummaryEl.innerHTML = discountSummaryHtml(cart);
    if (discountCodesEl) {
      discountCodesEl.innerHTML = discountPillsHtml(codes);
      discountCodesEl.classList.toggle('has-codes', codes.length > 0);
    }
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

    // Keep open Rabatt panel tall enough after pill updates
    var discItem = root && root.querySelector('.cart-discount[data-ge-acc-item]');
    if (discItem && discItem.classList.contains('is-open')) {
      var panel = discItem.querySelector('.details-content');
      if (panel) {
        panel.style.height = 'auto';
        panel.style.height = panel.scrollHeight + 'px';
      }
    }
  }

  function renderCart(cart) {
    lastCart = cart;
    var count = cart.item_count || 0;
    if (badgeEl) {
      badgeEl.textContent = String(count);
      badgeEl.classList.toggle('visually-hidden', count === 0);
    }
    if (footerEl) footerEl.classList.toggle('is-empty', count === 0);
    if (noteEl && typeof cart.note === 'string' && document.activeElement !== noteEl) {
      noteEl.value = cart.note;
    }
    updateHeaderBubbles(count);
    renderDiscounts(cart);

    if (!bodyEl) return;
    if (count === 0) {
      bodyEl.innerHTML =
        '<div class="cart-drawer--empty-state" data-ge-cart-empty>' +
          '<h2 class="cart-drawer--empty-state__title">Ihr Warenkorb ist leer</h2>' +
          '<p class="cart-drawer--empty-state__text">' +
            'Haben Sie ein Konto? ' +
            '<a href="/account/login" class="cart-drawer--empty-state__login">Melden Sie sich an</a>, ' +
            'damit es beim Checkout schneller geht.' +
          '</p>' +
          '<a href="/collections/skyvell" class="button cart-items__empty-button">Weiter einkaufen</a>' +
        '</div>';
      return;
    }
    var html = '<div class="cart-drawer__items cart-items--dividers" data-ge-cart-items>';
    (cart.items || []).forEach(function (item) { html += itemHtml(item); });
    html += '</div>';
    bodyEl.innerHTML = html;
  }

  function cartFromSectionHtml(html) {
    if (!html) return null;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-ge-cart-json]');
      var text = (el ? el.textContent : doc.body.textContent) || '';
      text = text.trim();
      if (!text) return null;
      return JSON.parse(text);
    } catch (err) {
      return null;
    }
  }

  function cartFromAjaxResponse(data) {
    if (!data) return null;
    if (data.item_count != null && Array.isArray(data.items)) return data;
    if (data.sections && data.sections[CART_SECTION]) {
      return cartFromSectionHtml(data.sections[CART_SECTION]);
    }
    return null;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: CART_SECTION,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); }).then(function (data) {
      if (data && data.errors) throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed');
      return cartFromAjaxResponse(data) || fetchCart();
    });
  }

  function updateNote(note) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note }),
    }).then(function (r) { return r.json(); });
  }

  function updateDiscountCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: CART_SECTION,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); });
  }

  function showDiscountError(msg) {
    var errEl = root && root.querySelector('[data-ge-discount-error]');
    if (!errEl) return;
    errEl.hidden = !msg;
    errEl.textContent = msg || '';
  }

  function currentDiscountCodes() {
    if (lastCart) return appliedDiscountCodes(lastCart);
    if (!discountCodesEl) return [];
    return Array.prototype.map
      .call(discountCodesEl.querySelectorAll('.cart-discount__pill[data-discount-code]'), function (el) {
        return el.getAttribute('data-discount-code');
      })
      .filter(Boolean);
  }

  function applyDiscountCode(code) {
    var existing = currentDiscountCodes();
    var codeLower = code.toLowerCase();
    
    var existingLower = existing.map(function(c) { return c.toLowerCase(); });
    if (existingLower.indexOf(codeLower) !== -1) return Promise.resolve(lastCart);

    return updateDiscountCodes(existing.concat([code])).then(function (data) {
      var cart = cartFromAjaxResponse(data);
      var flags = data && data.discount_codes;
      if (Array.isArray(flags)) {
        var entry = null;
        for (var i = 0; i < flags.length; i++) {
          if (flags[i].code.toLowerCase() === codeLower) { entry = flags[i]; break; }
        }
        if (entry && entry.applicable === false) {
          showDiscountError('Ungültiger Rabattcode');
          return cart || fetchCart();
        }
      }
      if (!cart) return fetchCart();

      var appliedLower = appliedDiscountCodes(cart).map(function(c) { return c.toLowerCase(); });
      if (appliedLower.indexOf(codeLower) === -1) {
        showDiscountError('Rabattcode nicht anwendbar');
      } else {
        showDiscountError('');
      }
      return cart;
    });
  }

  function removeDiscountCode(code) {
    var next = currentDiscountCodes().filter(function (c) { return c !== code; });
    return updateDiscountCodes(next).then(function (data) {
      showDiscountError('');
      return cartFromAjaxResponse(data) || fetchCart();
    });
  }

  function animateRowRemove(row) {
    return new Promise(function (resolve) {
      if (!row || row.classList.contains('removing')) {
        resolve();
        return;
      }
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        resolve();
        return;
      }
      var h = row.getBoundingClientRect().height;
      row.style.setProperty('--row-height', h + 'px');
      row.style.height = h + 'px';
      row.classList.add('removing');
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        row.removeEventListener('animationend', onEnd);
        resolve();
      }
      function onEnd(e) {
        if (e.target !== row) return;
        finish();
      }
      row.addEventListener('animationend', onEnd);
      window.setTimeout(finish, 320);
    });
  }

  /** Server confirms delete first; then collapse; then paint. */
  function removeLine(key) {
    var row = root.querySelector('.cart-items__table-row[data-ge-cart-key="' + key + '"]');
    return changeLine(key, 0).then(function (cart) {
      return animateRowRemove(row).then(function () {
        renderCart(cart);
      });
    });
  }

  /**
   * Open uses SSR / last paint (Tapirax-style cache), syncs in background.
   * Mutations wait for server cart before open/paint.
   */
  function openFromCache() {
    openDrawer();
    return fetchCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function refresh(open) {
    if (open) return openFromCache();
    return fetchCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function addToCartFromForm(form) {
    var fd = new FormData(form);
    fd.append('sections', CART_SECTION);
    fd.append('sections_url', window.location.pathname);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (err) { throw err; });
      }
      return r.json();
    }).then(function (data) {
      var cart = cartFromAjaxResponse(data);
      if (cart) return cart;
      return fetchCart();
    });
  }

  function onClick(e) {
    if (e.target.closest('[data-ge-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    var removeDiscount = e.target.closest('[data-ge-discount-remove]');
    if (removeDiscount && root.contains(removeDiscount)) {
      e.preventDefault();
      var discCode = removeDiscount.getAttribute('data-code');
      if (!discCode) return;
      removeDiscountCode(discCode).then(renderCart);
      return;
    }
    var minus = e.target.closest('[data-ge-qty-minus]');
    var plus = e.target.closest('[data-ge-qty-plus]');
    var remove = e.target.closest('[data-ge-cart-remove]');
    if (minus || plus) {
      var wrap = e.target.closest('.quantity-selector');
      var input = wrap && wrap.querySelector('[data-ge-qty-input]');
      if (!input) return;
      var next = Number(input.value || 0) + (plus ? 1 : -1);
      if (next < 0) next = 0;
      input.value = String(next);
      var key = input.getAttribute('data-key');
      if (next === 0) removeLine(key);
      else changeLine(key, next).then(renderCart);
      return;
    }
    if (remove) {
      removeLine(remove.getAttribute('data-key'));
    }
  }

  function onChange(e) {
    var input = e.target.closest('[data-ge-qty-input]');
    if (input && root.contains(input)) {
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      var key = input.getAttribute('data-key');
      if (qty === 0) removeLine(key);
      else changeLine(key, qty).then(renderCart);
    }
  }

  function interceptAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = (form.getAttribute('action') || form.action || '').toLowerCase();
      if (action.indexOf('/cart/add') === -1) return;
      if (form.getAttribute('data-ge-skip-drawer') === 'true') return;

      e.preventDefault();
      e.stopPropagation();

      var btn = form.querySelector('[data-add-to-cart-btn], [type="submit"]');
      if (btn) {
        btn.setAttribute('aria-busy', 'true');
        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';
      }

      // Wait for server cart (one round-trip via sections), then paint + open
      addToCartFromForm(form)
        .then(function (cart) {
          renderCart(cart);
          openDrawer();
        })
        .catch(function (err) {
          window.alert((err && (err.description || err.message)) || 'Artikel konnte nicht hinzugefügt werden.');
        })
        .finally(function () {
          form.removeAttribute('data-submitting');
          if (btn) {
            btn.removeAttribute('aria-busy');
            btn.style.opacity = '';
            btn.style.pointerEvents = '';
            var t = btn.querySelector('[data-btn-text]');
            var l = btn.querySelector('[data-btn-loading]');
            if (t) t.hidden = false;
            if (l) l.hidden = true;
          }
        });
    }, true);
  }

  function enhanceAccordions() {
    root.querySelectorAll('[data-ge-acc-item]').forEach(function (item) {
      var trigger = item.querySelector('.cart-note__summary, .cart-discount__summary');
      var panel = item.querySelector('.details-content');
      if (!trigger || !panel || item.getAttribute('data-ge-acc') === '1') return;
      item.setAttribute('data-ge-acc', '1');

      var animating = false;
      var open = item.classList.contains('is-open') || trigger.getAttribute('aria-expanded') === 'true';

      if (open) {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = 'auto';
        panel.style.overflow = 'hidden';
      } else {
        panel.style.height = '0px';
      }

      function runHeight(from, to, onDone) {
        panel.style.transition = 'none';
        panel.style.height = from;
        void panel.offsetHeight;
        panel.style.transition = '';
        requestAnimationFrame(function () {
          panel.style.height = to;
        });
        var done = false;
        function finish(e) {
          if (e && e.target !== panel) return;
          if (e && e.propertyName && e.propertyName !== 'height') return;
          if (done) return;
          done = true;
          panel.removeEventListener('transitionend', finish);
          onDone();
        }
        panel.addEventListener('transitionend', finish);
        window.setTimeout(finish, 350);
      }

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (animating) return;
        animating = true;

        if (open) {
          // Close — never touch native <details>; just height 0
          var h = panel.getBoundingClientRect().height;
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          open = false;
          runHeight(h + 'px', '0px', function () {
            panel.style.height = '0px';
            panel.style.overflow = 'hidden';
            animating = false;
          });
        } else {
          open = true;
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          panel.style.overflow = 'hidden';
          // Measure natural height of inner content (padding included inside)
          panel.style.transition = 'none';
          panel.style.height = 'auto';
          var target = panel.scrollHeight;
          panel.style.height = '0px';
          void panel.offsetHeight;
          panel.style.transition = '';
          runHeight('0px', target + 'px', function () {
            panel.style.height = target + 'px';
            animating = false;
          });
        }
      });
    });
  }

  function init() {
    root = document.querySelector('[data-ge-cart-drawer]');
    if (!root) return;

    try {
      var raw = document.getElementById('ge-cart-icons');
      if (raw) icons = JSON.parse(raw.textContent);
    } catch (e) {}

    bodyEl = root.querySelector('[data-ge-cart-body]');
    badgeEl = root.querySelector('[data-ge-cart-badge]');
    footerEl = root.querySelector('[data-ge-cart-footer]');
    subtotalEl = root.querySelector('[data-ge-cart-subtotal]');
    noteEl = root.querySelector('[data-ge-cart-note]');
    discountSummaryEl = root.querySelector('[data-ge-cart-discount-summary]');
    discountCodesEl = root.querySelector('[data-ge-discount-codes]');

    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    enhanceAccordions();

    if (noteEl) {
      var noteTimer;
      noteEl.addEventListener('input', function () {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(function () {
          updateNote(noteEl.value || '');
        }, 400);
      });
    }

    var discountForm = root.querySelector('[data-ge-discount-form]');
    if (discountForm) {
      discountForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = discountForm.querySelector('[data-ge-discount-input]');
        var code = ((input && input.value) || '').trim();
        if (!code) return;
        showDiscountError('');
        var btn = discountForm.querySelector('[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
        applyDiscountCode(code)
          .then(function (cart) {
            if (cart) renderCart(cart);
            if (input) input.value = '';
          })
          .catch(function () {
            showDiscountError('Rabattcode konnte nicht angewendet werden');
          })
          .finally(function () {
            if (btn) {
              btn.disabled = false;
              btn.removeAttribute('aria-busy');
            }
          });
      });
    }

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-ge-cart-open]');
      if (!opener) return;
      e.preventDefault();
      refresh(true);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) closeDrawer();
    });

    interceptAddToCart();
    interceptCartPageLinks();
    handleCartLanding();
    window.GeCartDrawer = { open: function () { return refresh(true); }, close: closeDrawer, refresh: function () { return refresh(false); } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
